<script setup>
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { ref, onBeforeMount, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLayout } from '@/layout/composables/layout';

const route = useRoute();
const router = useRouter();
const { user } = useUserSession();
const { layoutConfig, layoutState, setActiveMenuItem, toggleMenu, isHorizontal, isSlim, isSlimPlus, isDesktop, isOverlay, isStatic, openTab } = useLayout();

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  index: {
    type: Number,
    default: 0
  },
  root: {
    type: Boolean,
    default: true
  },
  parentItemKey: {
    type: String,
    default: null
  }
});

const isActiveMenu = ref(false);
const itemKey = ref(null);
const subMenuRef = ref(null);
const menuItemRef = ref(null);


onBeforeMount(() => {
  itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);

  const activeItem = layoutState.activeMenuItem;

  isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(itemKey.value + '-') : false;
  handleRouteChange(route.path);
});

watch(
  () => isActiveMenu.value,
  () => {
    // Only proceed if this is a menu that should display a submenu
    if (!((isSlim.value || isSlimPlus.value || isHorizontal.value) && isDesktop && props.root)) {
      return;
    }

    nextTick(() => {
      // Use refs to directly access the current menu item and its submenu
      const target = menuItemRef.value;
      const overlay = subMenuRef.value;

      if (target && overlay && isActiveMenu.value) {
        calculatePosition(overlay, target);
      }
    });
  }
);

watch(
  () => layoutState.activeMenuItem,
  (newVal) => {
    isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
  }
);

watch(
  () => layoutConfig.menuMode.value,
  () => {
    isActiveMenu.value = false;
  }
);

watch(
  () => layoutState.overlaySubmenuActive,
  (newValue) => {
    if (!newValue) {
      isActiveMenu.value = false;
    }
  }
);
watch(
  () => route.path,
  (newPath) => {
    if (!(isSlim.value || isSlimPlus.value || isHorizontal.value) && props.item.to && props.item.to === newPath) {
      setActiveMenuItem(itemKey);
    } else if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
      isActiveMenu.value = false;
    }
  }
);

const handleRouteChange = (newPath) => {
  if (!(isSlim.value || isSlimPlus.value || isHorizontal.value) && props.item.to && props.item.to === newPath) {
    setActiveMenuItem(itemKey);
  } else if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
    isActiveMenu.value = false;
  }
};

watch(() => route.path, handleRouteChange);

const itemClick = async (event, item) => {
  if (item.disabled) {
    event.preventDefault();
    return;
  }

  const { overlayMenuActive, staticMenuMobileActive } = layoutState;

  if ((item.to || item.url) && (staticMenuMobileActive || overlayMenuActive)) {
    toggleMenu();
  }

  if (item.command) {
    item.command({ originalEvent: event, item: item });
  }

  // add tab
  if (event.metaKey && item.to && (!item.data || !item.data.fullPage)) {
    router.push(item.to);
    openTab(item);
    event.preventDefault();
  }

  if (item.items) {
    if (props.root && isActiveMenu.value && (isSlim.value || isSlimPlus.value || isHorizontal.value)) {
      layoutState.overlaySubmenuActive = false;
      layoutState.menuHoverActive = false;

      return;
    }

    setActiveMenuItem(isActiveMenu.value ? props.parentItemKey : itemKey);

    if (props.root && !isActiveMenu.value && (isSlim.value || isSlimPlus.value || isHorizontal.value)) {
      layoutState.overlaySubmenuActive = true;
      layoutState.menuHoverActive = true;
      isActiveMenu.value = true;

      removeAllTooltips();
    }
  } else {
    if (!isDesktop) {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
    }

    if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
      layoutState.overlaySubmenuActive = false;
      layoutState.menuHoverActive = false;

      return;
    }

    setActiveMenuItem(itemKey);
  }
};

const onMouseEnter = () => {
  if (props.root && (isSlim.value || isSlimPlus.value || isHorizontal.value) && isDesktop) {
    if (!isActiveMenu.value && layoutState.menuHoverActive) {
      setActiveMenuItem(itemKey);
    }
  }
};
const removeAllTooltips = () => {
  const tooltips = document.querySelectorAll('.p-tooltip');
  tooltips.forEach((tooltip) => {
    tooltip.remove();
  });
};

const calculatePosition = (overlay, target) => {
  if (!overlay || !target) return;

  // Get complete information about target and overlay elements
  const targetRect = target.getBoundingClientRect();
  const vHeight = window.innerHeight;
  const vWidth = window.innerWidth;
  const oHeight = overlay.offsetHeight;
  const oWidth = overlay.offsetWidth;
  const topbarEl = document.querySelector('.layout-topbar');
  const topbarHeight = topbarEl?.offsetHeight || 0;

  // Count actual menu items in the submenu (excluding empty text nodes)
  const actualMenuItems = Array.from(overlay.children).filter(
    child => child.tagName && child.tagName.toLowerCase() === 'li'
  ).length;

  // Reset styles
  overlay.style.top = '';
  overlay.style.left = '';
  overlay.style.position = 'fixed'; // Use fixed positioning to avoid issues

  if (isSlim.value || isSlimPlus.value) {
    // Special handling for single-item submenus or 4th menu items
    const isSpecialCase = actualMenuItems <= 1 || props.index === 3; // 0-based index, so 4th item is index 3

    // Calculate vertical position - align with parent for best UX
    let topPosition;

    if (isSpecialCase) {
      // For special cases, align exactly with parent
      topPosition = targetRect.top;
    } else {
      // Normal calculation
      topPosition = targetRect.top;

      // Ensure submenu doesn't go below viewport
      if (topPosition + oHeight > vHeight) {
        // If submenu is too tall, align bottom with viewport bottom
        topPosition = Math.max(5, vHeight - oHeight - 5);
      }
    }

    // Set vertical position
    overlay.style.top = `${topPosition}px`;

    // Calculate horizontal position - always to the right of sidebar
    const parentRight = targetRect.right;

    // Check if there's enough space on the right
    if (parentRight + oWidth + 10 <= vWidth) {
      overlay.style.left = `${parentRight + 1}px`; // 1px gap
    } else {
      // If not enough space on right, position to left
      overlay.style.left = `${targetRect.left - oWidth - 1}px`;
    }

    // Force a browser reflow to ensure the submenu is properly positioned
    overlay.offsetHeight;
  }
};

const checkActiveRoute = (item) => {
  if (item?.items?.length > 0) {
    return item?.items?.some(item => route.path.includes(item.name));
  }
  return route.path.includes(item.name);
};
</script>

<template>
  <!-- <li ref="menuItemRef" :style="{ 'background-color': checkActiveRoute(item) ? 'var(--v-menuitem-hover-bg)' : 'transparent' }"
    :class="{ 'layout-root-menuitem': root, 'active-menuitem': isStatic || isOverlay ? !root && isActiveMenu : isActiveMenu}">
    <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">
      <span>{{ item.label }}</span> <em class="layout-menuitem-root-icon"></em>
    </div>
    <a v-if="(!item.to || item.items) && item.visible !== false" :href="item.url"
      @click="itemClick($event, item, index)" :class="item.class" :target="item.target" tabindex="0"
      @mouseenter="onMouseEnter" v-tooltip.hover="isSlim && root && !isActiveMenu ? item.label : null">
      <em :class="item.icon" class="layout-menuitem-icon"></em>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <em class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></em>
    </a>
    <router-link v-if="item.to && !item.items && item.visible !== false" @click="itemClick($event, item, index)"
      :class="[item.class, '!mb-0', { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="item.to"
      @mouseenter="onMouseEnter" v-tooltip.hover="(isSlim || isSlimPlus) && root && !isActiveMenu ? item.label : null">
      <div class="text-slate-800 dark:text-white flex pr-2 items-center ">
        <ThemedIcon :name="item.name" />
      </div>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <em class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></em>
    </router-link>

    <ul ref="subMenuRef" :class="{ 'layout-root-submenulist': root }" v-if="item.items && item.visible !== false">
      <AppMenuItem v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parentItemKey="itemKey"
        :root="false"></AppMenuItem>
    </ul>
  </li> -->
  <li
  ref="menuItemRef" class="rounded"
  :style="{ 'background-color': checkActiveRoute(item) ? 'var(--v-menuitem-hover-bg)' : 'transparent' }"
  :class="{
    'layout-root-menuitem': root,
    'active-menuitem': isStatic || isOverlay ? !root && isActiveMenu : isActiveMenu
  }"
  v-if="item.allowedUsers.includes(Number(user.userType))"
>
  <!-- Root menu item header -->
  <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">
    <template v-if="(!item.to || item.items) && item.visible !== false">
      <a
        :href="item.url"
        @click.prevent="itemClick($event, item, index)"
        :class="item.class"
        :target="item.target"
        tabindex="0"
        @mouseenter="onMouseEnter"
        v-tooltip.hover="isSlim && root && !isActiveMenu ? item.label : null"
      >
        <span class="layout-menuitem-text">{{ item.label }}</span>
      </a>
    </template>

    <template v-else-if="item.to && !item.items && item.visible !== false">
      <router-link
        @click="itemClick($event, item, index)"
        :class="[item.class, '!mb-0', { 'active-route': checkActiveRoute(item) }]"
        tabindex="0"
        :to="item.to"
        @mouseenter="onMouseEnter"
        v-tooltip.hover="(isSlim || isSlimPlus) && root && !isActiveMenu ? item.label : null"
      >
        <span class="layout-menuitem-text">{{ item.label }}</span>
        <em v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler"></em>
      </router-link>
    </template>
  </div>

  <!-- Regular menu items (non-root) -->
  <template v-if="(!item.to || item.items) && item.visible !== false">
    <a
      :href="item.url"
      @click.prevent="itemClick($event, item, index)"
      :class="item.class"
      :target="item.target"
      tabindex="0"
      @mouseenter="onMouseEnter"
      v-tooltip.hover="isSlim && root && !isActiveMenu ? item.label : null"
    >
      <em :class="item.icon" class="layout-menuitem-icon"></em>
      <span class="layout-menuitem-text" v-html="sanitizeHtml(item.label)"></span>
      <em v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler"></em>
    </a>
  </template>

  <template v-else-if="item.to && !item.items && item.visible !== false">
    <router-link
      @click="itemClick($event, item, index)"
      :class="[item.class, '!mb-0', { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      :to="item.to"
      @mouseenter="onMouseEnter"
      v-tooltip.hover="(isSlim || isSlimPlus) && root && !isActiveMenu ? item.label : null"
    >
      <em
        v-if="item.icon?.startsWith('pi ')"
        :class="item.icon"
        class="layout-menuitem-icon !text-xl"
      ></em>
      <div v-else class="text-slate-800 dark:text-white flex pr-2 items-center">
        <ThemedIcon :name="item.name" />
      </div>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <em v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler"></em>
    </router-link>
  </template>

  <!-- Submenu -->
  <ul
    v-if="item.items && item.visible !== false"
    ref="subMenuRef"
    :class="{ 'layout-root-submenulist': root }"
  >
    <AppMenuItem
      v-for="(child, i) in item.items"
      :key="`${itemKey}-${i}`"
      :index="i"
      :item="child"
      :parentItemKey="itemKey"
      :root="false"
    />
  </ul>
</li>
</template>

<style scoped>
.layout-root-submenulist {
  position: fixed;
  z-index: 999;
  display: none;
  min-width: 200px;
  padding: 0.5rem 0;
  background-color: var(--surface-overlay);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.active-menuitem > .layout-root-submenulist {
  display: block;
}
</style>
